import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { revokeOtherSessions, revokeResetTickets } from '../../server/utils/auth-revocation.js';
import { sendAccountDeletedEmail } from '../../server/utils/email.js';
import { deleteClassroomData } from '../api/delete-classroom.js';
import { NOTIFICATIONS_COLLECTION } from '../api/result-notifications.js';
import { AppFeedback } from '../app-feedpack.js';
import { Waitlist } from '../beta-access.js';
import { ClassMembership } from '../class-membership.js';
import { ClassRoom } from '../classroom.js';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { ExamResultRelease } from '../exam-result-release.js';
import { Feedback } from '../feedback.js';
import { Student } from '../student.js';
import { Submission } from '../submission.js';
import { Teacher } from '../teacher.js';
import { deleteExamCascade, removeStoredFile } from './delete-cascade.js';

/**
 * "Konto löschen" (DSGVO Art. 17) for teacher and student accounts — and for both at once: older accounts can have
 * both profiles, and everything of either role goes. Server only: db/api/delete-account.ts imports it dynamically
 * and refuses admin accounts before this is ever reached.
 *
 * RUNS AS THE USER, BEFORE THE SESSION IS SIGNED OUT: the exam cascade only deletes files the caller owns
 * (delete-cascade.ts `deleteOwnFiles`), and it finds the caller through the session.
 *
 * THE PROFILES GO LATE: classes, memberships, releases and the teacher's submissions point at `Teacher._id` /
 * `Student._id`, not at the account, so they are read first and removed only once nothing needs them any more.
 *
 * A TEACHER TAKES THEIR CLASSES WITH THEM: every class goes the way deleteClassroomMutation deletes one, so the
 * students lose the class, their membership and every result released to them; then every exam goes, with the
 * students' papers in it. The students' own accounts stay untouched.
 *
 * WHAT STAYS WITH THE TEACHER: the student's paper in a teacher's exam — scan, the name read from it, grades — is the
 * school's record. Only the link to the account (`Submission.studentID`) and what was released to the student go.
 *
 * NOT COVERED: server logs that carry the `userID`, backups, and anything a job that is running right now writes
 * afterwards (see delete-cascade.ts).
 */
export async function deleteAccount(userID: string): Promise<void> {
  const users = await getRawCollection(AdornisUser._collectionName);
  const user = await users.findOne({ _id: userID as any }, { projection: { _id: 1, email: 1 } });
  if (!user) return;
  const email = typeof user.email === 'string' ? user.email : '';
  const students = await getRawCollection(Student._collectionName);
  const studentID = (await students.findOne({ userID }, { projection: { _id: 1 } }))?._id as string | undefined;
  const teachers = await getRawCollection(Teacher._collectionName);
  const teacherID = (await teachers.findOne({ userID }, { projection: { _id: 1 } }))?._id as string | undefined;

  // A teacher's classes first: their releases go with read access through `classRoomID`, before the exams do.
  let classCount = 0;
  if (teacherID) {
    const classes = await (await getRawCollection(ClassRoom._collectionName))
      .find({ teacherID }, { projection: { _id: 1 } })
      .toArray();
    for (const classroom of classes) await deleteClassroomData(String(classroom._id), teacherID);
    classCount = classes.length;
    // Rows that still name the teacher without a class behind them.
    await (await getRawCollection(ClassMembership._collectionName)).deleteMany({ teacherID });
  }

  // Own exams — a teacher's class exams, a student's self-practice and online hand-ins — with everything on them.
  const ownExams = await (await getRawCollection(Exam._collectionName))
    .find({ userID }, { projection: { _id: 1 } })
    .toArray();
  for (const exam of ownExams) await deleteExamCascade(String(exam._id));

  // Chats without an exam, and the feedback that copied them.
  const conversations = await getRawCollection(Conversation._collectionName);
  const conversationIds = (await conversations.find({ userID }, { projection: { _id: 1 } }).toArray()).map(c =>
    String(c._id),
  );
  if (conversationIds.length) {
    await (await getRawCollection(Feedback._collectionName)).deleteMany({ conversationId: { $in: conversationIds } });
  }
  await conversations.deleteMany({ userID });

  // What other teachers hold about the account as a student. Read access is pulled by account id directly:
  // setReleaseFileReaders maps through the profile and would skip a student whose profile is gone.
  const files = await getRawCollection(AdornisFile._collectionName);
  await files.updateMany({ readerUserIDs: userID }, { $pull: { readerUserIDs: userID } } as any);
  if (studentID) {
    await (await getRawCollection(ExamResultRelease._collectionName)).deleteMany({ studentID });
    await (await getRawCollection(NOTIFICATIONS_COLLECTION)).deleteMany({ studentID });
    // A stale id would be proposed again on the next release and fail there as "outside the class".
    await (await getRawCollection(Submission._collectionName)).updateMany({ studentID }, { $unset: { studentID: '' } });
    await (await getRawCollection(ClassMembership._collectionName)).deleteMany({ studentID });
  }

  // Every file the account owns, including the ones nothing refers to any more (screenshots, unused uploads).
  const ownFiles = await files.find({ ownerUserID: userID }, { projection: { _id: 1 } }).toArray();
  for (const file of ownFiles) await removeStoredFile(String(file._id));

  await (await getRawCollection(AppFeedback._collectionName)).deleteMany({ userId: userID });
  if (email) {
    const escaped = email.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    await (
      await getRawCollection(Waitlist._collectionName)
    ).deleteMany({ email: { $regex: `^${escaped}$`, $options: 'i' } });
  }

  await revokeResetTickets(userID);
  await students.deleteMany({ userID });
  await teachers.deleteMany({ userID });
  await revokeOtherSessions(userID);
  await users.deleteOne({ _id: userID as any });

  logger.info(
    {
      context: 'deleteAccount',
      userID,
      teacher: Boolean(teacherID),
      student: Boolean(studentID),
      classes: classCount,
      exams: ownExams.length,
      conversations: conversationIds.length,
      files: ownFiles.length,
    },
    'Account deleted',
  );

  // The account is gone either way; a failed mail is logged inside sendMail.
  if (email) await sendAccountDeletedEmail(email, userID, teacherID ? 'teacher' : 'student').catch(() => undefined);
}
